import { cloneDeep, get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import withDraft from '@/utils/draftHelper';
import findResult, {
  assignIfExists,
  assignProps,
  findWithReadStatus,
  hasKey,
  isEmpty,
  removeItemByCode,
} from '@/utils/resultsHelper';
import { saveStates, states } from '@/constants/lifecycle';
import { responseError } from '@/utils/errors';
import { alignFieldsToForm } from '@/forms/state/state';
import actions, { datasetLifeCycle, datasetLists, datasetSteps } from '../constants';
import {
  activeDataset,
  assignEditProps,
  closeDatasetState,
  datasetDirtyFlags,
  datasetSourceAndStepAttributes,
  mapResult,
  markDatasetAsDirty,
  newDataset,
  newDatasetProperties,
  nextDatasetStepIndex,
  openDataset,
  pruneDatasetErrors,
  resetDataset,
  showDatasetErrorState,
  switchOffDatasetPreview,
} from './state';
import findDataset, { findAllDatasets } from './shared';
import customActionsInitialState from '../links/state/state';
import attachmentsInitialState from '../attachments/state/state';
import { collectionsInitialState } from '../collections/state/state';
import { addUUIDsToDictionaries } from '../dictionaries/helper';
import { cloneDictionaries, firstDictionary, primaryDictionary } from '../dictionaries/state/state';
import { cloneDatasetSettings } from '../settings/state/state';
import { cloneCatalogue } from '../catalogue/state/state';
import { datasetPermissions } from '../sharing/constants';

const closeDataset = withDraft((draft, action) => { closeDatasetState(draft, action); });

const createDataset = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  item.saveStatus = states.PENDING;
  item.saveErrors = [];
});

const datasetSettingsChanged = withDraft((draft, { code, result }) => {
  const templateCode = result.catalogue_definition_code;
  findAllDatasets(draft, { items: [{ code }] }).forEach((category) => {
    const item = findResult(draft.datasets, category, code);
    const currentTemplate = get(item, 'settings.catalogue_definition_code', templateCode);
    const wizardTemplate = get(item, 'steps.templateCode', templateCode) || templateCode;
    if (currentTemplate !== templateCode || wizardTemplate !== currentTemplate) {
      resetDataset(item);
      assignIfExists(item, { steps: { activeStepIndex: 0, templateCode } });
    }
    assignIfExists(item, { settings: result });
  });
});

const deleteDataset = withDraft((draft, action) => {
  findAllDatasets(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.datasets, category, code);
      if (item) {
        item.destroyStatus = states.PENDING;
        item.destroyErrors = [];
      }
    });
  });
});

const destroyDataset = withDraft((draft, action) => {
  draft.deleteDatasetOpen = false;
  findAllDatasets(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.datasets, category, code);
      if (item) {
        draft.datasets[category].results = removeItemByCode(draft.datasets[category].results, item);
      }
    });
  });
});

const destroyDatasetError = withDraft((draft, action) => {
  draft.deleteDatasetOpen = false;
  findAllDatasets(draft, action).forEach((category) => {
    action.items.forEach(({ code, error }) => {
      const item = findResult(draft.datasets, category, code);
      if (item) {
        item.destroyStatus = states.ERROR;
        item.destroyErrors = [error];
      }
    });
  });
});

const editDataset = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  item.isDirty.catalogue = assignEditProps(item, action.template);
  switchOffDatasetPreview(item);
});

const fetchDataset = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
    item.customActions = customActionsInitialState();
    item.attachments = attachmentsInitialState();
    item.collections = collectionsInitialState();
  } else {
    draft.datasets[action.category].results.push(openDataset(action));
  }
});

const hideDeleteDataset = withDraft((draft) => {
  draft.deleteDatasetOpen = false;
  draft.datasets.deletion.results = [];
});

const hideSaveDataset = withDraft((draft) => { draft.activeDataset = activeDataset(); });

const newDatasetHandler = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  draft.activeDataset = activeDataset();
  item.previousCode = item.code;
  item.copy = {};

  const dictionaries = addUUIDsToDictionaries(action.result.dictionaries);
  const dictionary = firstDictionary(dictionaries);

  assignIfExists(item, action.result);
  item.catalogue = action.result.catalogue;
  item.dictionaries = dictionaries;
  item.activeDictionaryName = dictionary.name;
  item.activeDictionaryUUID = dictionary.uuid;
  item.saveStatus = states.SUCCESS;
  item.readStatus = states.SUCCESS;
  item.saveErrors = [];
  item.readErrors = [];
  item.isDirty = datasetDirtyFlags();
  item.isNew = false;
  item.isEditing = false;

  findWithReadStatus(draft.datasets, datasetLists, states.SUCCESS).forEach((category) => {
    draft.datasets[category].readStatus = states.PENDING;
    draft.datasets[category].readErrors = [];
  });

  if (item.steps.wizardOn) {
    item.steps.activeStepIndex = nextDatasetStepIndex(item, action);
  }
  switchOffDatasetPreview(item);
});

const newDatasetError = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  assignIfExists(draft.activeDataset, {
    saveStatus: states.ERROR,
    saveErrors: [responseError(action)],
  });
  if (item.steps.wizardOn) {
    item.saveStatus = states.ERROR;
    item.saveErrors = [responseError(action)];
  }
});

const newDatasetFromSource = withDraft((draft, action) => {
  draft.newDatasetCode = uuidv4();
  draft.datasets[action.category].results.push(newDataset(action, !draft.wizardOff));
});

const refreshDatasetSettings = withDraft((draft, { code, result }) => {
  findAllDatasets(draft, { items: [{ code }] }).forEach((category) => {
    const item = findResult(draft.datasets, category, code);
    assignIfExists(item, { settings: result });
  });
});

const showCloneDataset = withDraft((draft, {
  category,
  code,
  template,
  result,
}) => {
  const item = findDataset(draft, { category, code });
  const dictionaries = addUUIDsToDictionaries(cloneDictionaries(result));
  const dictionary = firstDictionary(dictionaries);
  markDatasetAsDirty(item, datasetSteps.FIRST_SECTION);
  if (dictionaries.length > 0) {
    markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries');
  }

  assignProps(item, {
    settings: cloneDatasetSettings(result),
    catalogue: cloneCatalogue(result),
    dictionaries,
    readStatus: states.SUCCESS,
    readErrors: [],
    ...saveStates,
    subscriptionsStatus: states.SUCCESS,
    subscriptionsErrors: [],
    activeDictionaryName: dictionary.name,
    activeDictionaryUUID: dictionary.uuid,
    created_at: Date.now(),
    updated_at: Date.now(),
    steps: { code: null },
  });
  if (template?.readStatus === states.SUCCESS) {
    assignEditProps(item, template);
  }
});

const showDataset = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  const dictionaries = addUUIDsToDictionaries(action.result.dictionaries);
  const dictionary = primaryDictionary({ dictionaries });

  assignProps(item, {
    ...action.result,
    dictionaries,
    readStatus: states.SUCCESS,
    readErrors: [],
    ...saveStates,
    subscriptionsStatus: states.SUCCESS,
    subscriptionsErrors: [],
    isEditing: false,
    isDirty: datasetDirtyFlags(),
    activeDictionaryName: dictionary.name,
    activeDictionaryUUID: dictionary.uuid,
  });
});

const showDatasetError = withDraft((draft, action) => { showDatasetErrorState(draft, action); });

const showDeleteDataset = withDraft((draft, action) => {
  draft.deleteDatasetOpen = true;
  draft.datasets.deletion.results = [action.dataset];
});

const showNewDataset = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  draft.newDatasetCode = uuidv4();
  item.created_at = Date.now();
  item.updated_at = Date.now();
  item.readStatus = states.SUCCESS;
  item.settings.catalogue_definition_code = action.templateCode;
  item.steps.code = null;
  if (action.template?.readStatus === states.SUCCESS) {
    assignEditProps(item, action.template);
  }
});

const showSaveDataset = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  assignProps(draft.activeDataset, newDatasetProperties(item, action.template));
});

const updateActiveDatasetProperties = withDraft((draft, action) => {
  assignIfExists(draft.activeDataset, action.properties);
  draft.activeDataset.saveErrors = pruneDatasetErrors(draft.activeDataset.saveErrors, action);
});

const updateCurrentDatasetPermissions = withDraft((draft, { code, permissions }) => {
  const canEdit = permissions[datasetPermissions.UPDATE];

  findAllDatasets(draft, { items: [{ code }] }).forEach((category) => {
    const item = findResult(draft.datasets, category, code);
    if (!canEdit) {
      resetDataset(item, true);
    }
    if (item.asViewer?.on) {
      item.asViewer.permissions = permissions;
    } else {
      item.permissions = permissions;
    }
  });
});

const updateDataset = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  item.saveStatus = states.PENDING;
  item.saveErrors = [];
});

const updateDatasetCancel = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  resetDataset(item, true);
  switchOffDatasetPreview(item);
});

const updateDatasetCatalogueSuccess = withDraft((draft, {
  code,
  result,
  template,
  allowAttachments,
}) => {
  findAllDatasets(draft, { items: [{ code }] }).forEach((category) => {
    const item = findResult(draft.datasets, category, code);
    const mappedResult = mapResult(result);
    delete mappedResult.dictionaries;
    const isDirty = item.isDirty || datasetDirtyFlags();
    isDirty.catalogue = false;

    assignIfExists(item, {
      ...mappedResult,
      saveStatus: states.SUCCESS,
      saveErrors: [],
      isDirty,
    });
    if (hasKey(item, 'copy')) {
      item.copy.catalogue = cloneDeep(result.catalogue);
      alignFieldsToForm(item.catalogue, template);
    }
    if (hasKey(item, 'catalogue')) {
      item.catalogue = mappedResult.catalogue;
    }
    if (hasKey(item, 'asViewer')) {
      switchOffDatasetPreview(item);
    }
    if (hasKey(item, 'steps') && item.steps.wizardOn) {
      item.steps.activeStepIndex = nextDatasetStepIndex(item, { template, allowAttachments });
    }
  });
});

const updateDatasetError = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  assignIfExists(item, {
    saveStatus: states.ERROR,
    saveErrors: [responseError(action)],
  });
  switchOffDatasetPreview(item);
});

const updateDatasetProperties = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  if (assignProps(item, action.properties, datasetLifeCycle)) {
    markDatasetAsDirty(item, datasetSteps.FIRST_SECTION);
  }
});

const updateDatasetSuccess = withDraft((draft, {
  code,
  result,
  template,
  allowAttachments,
}) => {
  findAllDatasets(draft, { items: [{ code }] }).forEach((category) => {
    const item = findResult(draft.datasets, category, code);
    const dictionaries = addUUIDsToDictionaries(result.dictionaries);
    const dictionary = dictionaries.find((i) => i.name === item.activeDictionaryName);
    const activeDictionaryUUID = dictionary ? dictionary.uuid : null;
    const mappedResult = mapResult(result);

    assignIfExists(item, {
      ...mappedResult,
      saveStatus: states.SUCCESS,
      saveErrors: [],
      isDirty: datasetDirtyFlags(),
      isEditing: false,
      activeDictionaryUUID,
    });
    if (hasKey(item, 'copy')) {
      item.copy = {};
    }
    if (hasKey(item, 'dictionaries')) {
      item.dictionaries = dictionaries;
    }
    if (hasKey(item, 'primary_dictionary')) {
      item.primary_dictionary = primaryDictionary({ dictionaries })?.code;
    }
    if (hasKey(item, 'catalogue')) {
      item.catalogue = mappedResult.catalogue;
    }
    if (hasKey(item, 'asViewer')) {
      switchOffDatasetPreview(item);
    }
    if (hasKey(item, 'steps') && item.steps.wizardOn) {
      const nextStep = nextDatasetStepIndex(item, { template, allowAttachments });
      if (isEmpty(nextStep)) {
        item.steps = datasetSourceAndStepAttributes({ code: item.code });
      } else {
        item.steps.activeStepIndex = nextStep;
      }
    }
  });
});

const updateDatasetValidationErrors = withDraft((draft, { category, code, errors }) => {
  const item = findDataset(draft, { category, code });

  item.saveErrors = errors;
  if (errors.length > 0) {
    item.saveStatus = states.ERROR;
    switchOffDatasetPreview(item);
  }
});

export default {
  [actions.CLOSE_DATASET]: closeDataset,
  [actions.CREATE_DATASET]: createDataset,
  [actions.DATASET_SETTINGS_CHANGED]: datasetSettingsChanged,
  [actions.DELETE_DATASET]: deleteDataset,
  [actions.DESTROY_DATASET]: destroyDataset,
  [actions.DESTROY_DATASET_ERROR]: destroyDatasetError,
  [actions.EDIT_DATASET]: editDataset,
  [actions.FETCH_DATASET]: fetchDataset,
  [actions.HIDE_DELETE_DATASET]: hideDeleteDataset,
  [actions.HIDE_SAVE_DATASET]: hideSaveDataset,
  [actions.NEW_DATASET]: newDatasetHandler,
  [actions.NEW_DATASET_ERROR]: newDatasetError,
  [actions.NEW_DATASET_FROM_SOURCE]: newDatasetFromSource,
  [actions.REFRESH_DATASET_SETTINGS]: refreshDatasetSettings,
  [actions.SHOW_CLONE_DATASET]: showCloneDataset,
  [actions.SHOW_DATASET]: showDataset,
  [actions.SHOW_DATASET_ERROR]: showDatasetError,
  [actions.SHOW_DELETE_DATASET]: showDeleteDataset,
  [actions.SHOW_NEW_DATASET]: showNewDataset,
  [actions.SHOW_SAVE_DATASET]: showSaveDataset,
  [actions.UPDATE_ACTIVE_DATASET_PROPERTIES]: updateActiveDatasetProperties,
  [actions.UPDATE_CURRENT_DATASET_PERMISSIONS]: updateCurrentDatasetPermissions,
  [actions.UPDATE_DATASET]: updateDataset,
  [actions.UPDATE_DATASET_CANCEL]: updateDatasetCancel,
  [actions.UPDATE_DATASET_CATALOGUE_SUCCESS]: updateDatasetCatalogueSuccess,
  [actions.UPDATE_DATASET_ERROR]: updateDatasetError,
  [actions.UPDATE_DATASET_PROPERTIES]: updateDatasetProperties,
  [actions.UPDATE_DATASET_SUCCESS]: updateDatasetSuccess,
  [actions.UPDATE_DATASET_VALIDATION_ERRORS]: updateDatasetValidationErrors,
};
